import type { VNode } from 'vue'
import { computed, h, ref, shallowRef, watch } from 'vue'
import { defineStore } from 'pinia'
import { layoutConfig } from '@layouts'
import { themeConfig } from '@themeConfig'
import ruLogo from '@images/logo_ru.svg?raw'

const LOGO_STYLE = 'line-height:0; color: rgb(var(--v-global-theme-primary))'
const isRuOnly = import.meta.env.VITE_APP_RU === 'only'
const ruTitle = 'SkillsTrainer'

export const productName = isRuOnly ? 'SkillsTrainer' : 'The Reflection'

interface OrganizationMeta {
  logo?: string | null
  title?: string | null
  browserTitle?: string | null
  favicon?: string | null
}

const filledOrEmpty = (value?: string | null): string => (value && value !== 'null') ? value : ''

const svgToVNode = (svg?: string | null): VNode | null => {
  if (!svg || !svg.length)
    return null

  return h('div', { innerHTML: svg, style: LOGO_STYLE })
}

export const useBrandingStore = defineStore('branding', () => {
  const defaultLogo = shallowRef<VNode | null>(isRuOnly ? svgToVNode(ruLogo) : (themeConfig.app.logo ?? null))
  const defaultTitle = ref<string>(isRuOnly ? ruTitle : themeConfig.app.title ?? '')

  const logo = shallowRef<VNode | null>(defaultLogo.value)
  const title = ref<string>(defaultTitle.value)
  const organizationTitle = ref<string>('')
  const organizationBrowserTitle = ref<string>('')
  const organizationFavicon = ref<string>('')

  const organizationLogo = computed<VNode | null>(() => logo.value === defaultLogo.value ? null : logo.value)

  const displayLogo = computed<VNode | null>(() => logo.value ?? defaultLogo.value)
  const displayTitle = computed<string>(() => {
    if (title.value && title.value.length && title.value === 'null')
      return ''
    if (title.value && title.value.length)
      return title.value

    return defaultTitle.value
  })

  const setLogoVNode = (node: VNode | null) => {
    logo.value = node
  }

  const setLogoFromSvg = (svg?: string | null) => {
    logo.value = svgToVNode(svg)
  }

  const setTitle = (value?: string | null) => {
    if (typeof value === 'string')
      title.value = value
    else
      title.value = ''
  }

  const applyMetaBranding = (meta?: OrganizationMeta) => {
    setLogoFromSvg(meta?.logo)
    organizationTitle.value = filledOrEmpty(meta?.title)
    organizationBrowserTitle.value = filledOrEmpty(meta?.browserTitle)
    organizationFavicon.value = filledOrEmpty(meta?.favicon)

    if (meta?.title && meta.title.length)
      title.value = meta.title
    else if (meta?.logo && meta.logo.length)
      title.value = ''
    else
      title.value = defaultTitle.value
  }

  const resetBranding = () => {
    logo.value = defaultLogo.value
    title.value = defaultTitle.value
  }

  watch(displayLogo, val => {
    layoutConfig.app.logo = val ?? defaultLogo.value ?? null
  }, { immediate: true })

  watch(displayTitle, val => {
    layoutConfig.app.title = val
  }, { immediate: true })

  return {
    defaultLogo,
    defaultTitle,
    logo,
    title,
    organizationLogo,
    organizationTitle,
    organizationBrowserTitle,
    organizationFavicon,
    displayLogo,
    displayTitle,
    setLogoVNode,
    setLogoFromSvg,
    setTitle,
    applyMetaBranding,
    resetBranding,
  }
})

export const brandLogoStyle = LOGO_STYLE
export const buildBrandLogoVNode = (svg: string): VNode => h('div', { innerHTML: svg, style: LOGO_STYLE })
